/** Changelog — each entry is shown in the "What's New" popup when a user
 *  opens the app after an update.  Add a new entry at the TOP of the array
 *  each time the version in package.json is bumped.
 *
 *  `version`  must match the `version` field in package.json exactly.
 *  `date`     is a display string shown in the modal (e.g. 'May 2026').
 *  `features` new capabilities visible to the user.
 *  `fixes`    bug fixes and corrections (omit the array if none). */

export interface ChangelogEntry {
  version: string;
  date: string;
  features?: string[];
  fixes?: string[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '0.17.9',
    date: 'June 2026',
    features: [
      'Export modal: added MP4 video format option via FFmpeg.wasm. First render loads FFmpeg (≈30MB) from CDN, taking 30–60 seconds; subsequent renders use the cached FFmpeg instance.',
    ],
  },
  {
    version: '0.17.8',
    date: 'June 2026',
    fixes: [
      'Export modal: simplified output options — removed full-composite vs. selected-layer choice, now always renders the full composite to all formats (PNG sequence and WebM video).',
    ],
  },
  {
    version: '0.17.7',
    date: 'June 2026',
    fixes: [
      'Stamp tool: tint behavior is now automatic — predefined stamps always use the brush color tint, while uploaded and pasted stamps are never tinted. The tint button has been removed from the toolbar.',
    ],
  },
  {
    version: '0.17.6',
    date: 'July 2026',
    features: [
      'Added "Styleframe" predefined camera position',
    ]
  },
  {
    version: '0.17.5',
    date: 'June 2026',
    features: [
      'Layer reordering is now supported in the timeline: drag a layer\'s timing bar up or down to swap it with the layer above or below. Layer order can also be changed by dragging layers in the Layers panel (this also works for multi-selected layers).',
    ],
    fixes: [
      'Fixed a bug where opening a saved project could fail to restore the previous texture resolution, causing new layers to be created at the default 2K resolution regardless of the original project settings.',
    ],
  },
  {
    version: '0.17.4',
    date: 'May 2026',
    features: [
      'Clear Layer button added to the top of the Layers panel.',
      'Backspace hotkey deletes the selected layer; Alt+Backspace clears it (erases painted content).',
    ],
    fixes: [
      'Removed Fill All and Clear buttons from the Fill tool context strip (actions now accessible via hotkeys and the Layers panel button).',
    ],
  },
  {
    version: '0.17.3',
    date: 'May 2026',
    fixes: [
      'Gradient fill now correctly uses all color stops defined in the color picker, not just the first and last.',
    ],
  },
  {
    version: '0.17.2',
    date: 'May 2026',
    features: [
      'Fill tool modes renamed: "Contiguous" is now "Island", "Global" is now "Non-Contiguous". A new "Global" mode fills the entire layer regardless of existing content or tolerance.',
      'Gradient fill is now spherically aware: gradients are projected in 3D along the sphere surface rather than in flat UV space, so horizontal gradients no longer pinch at the poles.',
    ],
  },
  {
    version: '0.17.1',
    date: 'May 2026',
    fixes: [
      'Performance: keyframe writes no longer cause the entire Timeline to re-render; only the affected track row updates.',
      'Performance: eliminated per-frame Matrix4 allocations in the layer transform cache.',
    ],
  },
  {
    version: '0.17.0',
    date: 'May 2026',
    features: [
      'Layer hierarchy: drag any layer onto another to make it a child. Child layers inherit the parent\'s visibility, lock, rotation, translation, and opacity as additive offsets. Click the unparent button (◧ fold icon) on a child layer to remove the parent link.',
    ],
  },
  {
    version: '0.16.7',
    date: 'May 2026',
    features: [
      'Hold Shift while dragging a layer start/end handle or any keyframe to snap it directly to the current time marker.',
    ],
  },
  {
    version: '0.16.6',
    date: 'May 2026',
    fixes: [
      'Fixed a bug where saving a project immediately after painting could write stale (blank) layer data, causing layers to appear empty when the file was reloaded.',
    ],
  },
  {
    version: '0.16.5',
    date: 'May 2026',
    features: [
      'Fill tool now performs a proper flood fill on click — fills contiguous area or all matching pixels (Global mode) within a configurable color tolerance.',
      'Fill context strip has Contiguous / Global mode toggles and a Tolerance slider. The old full-layer fill is still available as \"Fill All\".',
    ],
  },
  {
    version: '0.16.4',
    date: 'May 2026',
    features: [
      'Hotkey reference HUD: a compact overlay in the bottom-right of the 3D viewport shows keyboard shortcuts for the active tool. Toggle with H or View → Show Hotkey Reference.',
    ],
  },
  {
    version: '0.16.3',
    date: 'May 2026',
    fixes: [
      'Inverse stamp/text projection now applies a nonlinear radial concave warp (r\' = r^k) around the camera-facing center, preserving angle while compressing the center inward for a deeper interior illusion.',
    ],
  },
  {
    version: '0.16.2',
    date: 'May 2026',
    fixes: [
      'Inverse projection now also drives draw, erase, and blur with back-hemisphere camera-ray mapping, aligning stroke preview/commit behavior with stamp and text inverse mode.',
    ],
  },
  {
    version: '0.16.1',
    date: 'May 2026',
    fixes: [
      'Inverse projection for stamp and text tools was rebuilt from scratch: previews/commits now render again, and inverse mapping now uses back-hemisphere camera-ray hits with a consistenenter at cnchor.',
    ],
  },
  {
    version: '0.16.0',
    date: 'May 2026',
    features: [
      'Camera FOV is now keyable in the timeline alongside X, Y, Z. FOV keyframes appear as a red sub-row when the camera track is expanded and interpolate smoothly during playback/scrub.',
    ],
  },
  {
    version: '0.15.9',
    date: 'May 2026',
    features: [
      "What's New changelog is now accessible from Help → What's New, showing the full version history.",
    ],
  },
  {
    version: '0.15.8',
    date: 'May 2026',
    features: [
      'Predefined cameras now store an FOV value (default 30). Snapping to a predefined camera applies its FOV, and pressing F to re-centre also resets FOV to 30.',
    ],
  },
  {
    version: '0.15.7',
    date: 'May 2026',
    features: [
      'Ripple Opacity tool now splits the timeline evenly across all layers by setting each layer\'s timing bar start and end points rather than writing opacity keyframes.',
    ],
  },
  {
    version: '0.15.6',
    date: 'May 2026',
    features: [
      'Timeline: every layer now has a timing bar spanning its active frames. Drag the left or right edge to trim when the layer appears or disappears; drag the centre of the bar to shift the layer in time (all keyframes move with it).',
      'Layer visibility is now driven by the timing bar instead of keyframeable visibility — the eye icon still toggles layers on/off globally, but the V keyframe channel has been removed.',
    ],
  },
  {
    version: '0.15.5',
    date: 'May 2026',
    features: [
      'Pasting an image (Ctrl+V outside stamp mode, or “Paste as Layer” in the stamp picker) now always creates a new layer for the image.',
      'Pasting a 2:1 equirectangular image immediately commits it as a new layer without showing the sizing overlay.',
    ],
  },
  {
    version: '0.15.4',
    date: 'May 2026',
    fixes: [
      'Draw tools: Smooth slider now averages input points via exponential moving average, visibly removing digitizer jitter and hand tremor from strokes.',
    ],
  },
  {
    version: '0.15.3',
    date: 'May 2026',
    features: [
      'Stamp and text tools: ghost previews now show all mirrored dup positions (diptych, triptych, quad) while hovering, matching the draw tool behaviour.',
    ],
  },
  {
    version: '0.15.2',
    date: 'May 2026',
    features: [
      '3D viewport: camera field-of-view (FOV) control added to the camera panel. Adjustable from 5\u00b0 to 150\u00b0, persisted across sessions. Hidden in orthographic mode.',
    ],
  },
  {
    version: '0.15.1',
    date: 'May 2026',
    features: [
      'Stamp tool: new \u201cTint\u201d button in the toolbar. When enabled, stamped images are color-multiplied by the active brush color; when off (default), images stamp exactly as imported.',
    ],
  },
  {
    version: '0.15.0',
    date: 'May 2026',
    fixes: [
      'The background layer encoder is now cancelled at the start of any drag gesture (rotate, translate, draw), preventing it from firing mid-interaction.',
    ],
  },
  {
    version: '0.14.8',
    date: 'May 2026',
    features: [
        'Blur tool: inner reticle ring now shows the kernel radius as a fraction of brush size.',
        'Stamp and text tools: Opacity slider added to the context toolbar.',
        'First-time users now start with Architecture overlay on, Manipulator off, 3K resolution, and Diptych mode.',
        'What\'s New popup: shows changes since the user\'s last-seen version on every update.',
    ],
    fixes: [
        'All tool reticles remain visible while adjusting size, strength, or radius via hotkeys.',
        'Blur brush: smoother results at large radii (25-tap Gaussian kernel, 4× denser sampling).',
        'Scrub reticle labels now always match the name of the slider being adjusted (Size, Strength, Radius, Rotation, Opacity, Layer).',
    ],
  },
];
